export const diagnosticReportConfig = {
  title: "Diagnostic Report",
  reportType: "Diagnostic Report",
  hiType: "DiagnosticReport",
  prefix: "DR",
  compactTable: true,
  includeService: true,
  serviceLabel: "Diagnostic Service / Test",
  includeDoctor: true,
  requireDoctor: true,
  doctorLabel: "Reporting Doctor",
  requireDate: true,
  fileRequired: true,
  fileLabel: "Diagnostic Report File (PDF/Image)",
  tableColumns: [
    {
      key: "appointmentId",
      label: "Appt ID",
      maxChars: 14,
      render: (item) => item?.appointmentId || item?.sourceAppointmentId || "-",
    },
    {
      key: "consultationDate",
      label: "Appt Date",
      render: (item, helpers) => helpers.formatDate(item?.consultationDate),
    },
    {
      key: "service",
      label: "Service",
      maxChars: 16,
      render: (item, helpers) =>
        item?.serviceName || helpers.getServiceNameById(item?.service),
    },
    {
      key: "reportTitle",
      label: "Service Details",
      maxChars: 30,
      render: (item) => item?.reportTitle || "-",
    },
  ],
  fields: [
    {
      key: "appointmentId",
      label: "Appointment ID",
      placeholder: "e.g. A26-18454",
    },
    {
      key: "reportTitle",
      label: "Service Details / Report Title",
      required: true,
      placeholder: "e.g. Chest X-ray PA view impression / CBC panel summary",
    },
    {
      key: "remark",
      label: "Diagnostic Findings / Conclusion",
      type: "textarea",
      rows: 3,
      required: true,
      placeholder: "Enter key findings, impression, and advice",
    },
  ],
  summaryColumn: {
    label: "Service Details",
    render: (item) => item?.remark || item?.reportTitle || "-",
  },
  buildDisplay: (report, helpers) =>
    `Diagnostic Report - ${helpers.getServiceNameById(report?.service)} - ${
      report?.consultationDate
        ? new Date(report.consultationDate).toLocaleDateString("en-GB")
        : "-"
    }`,
};

export const dischargeSummaryConfig = {
  title: "Discharge Summary",
  reportType: "Discharge Summary",
  hiType: "DischargeSummary",
  prefix: "DS",
  dateLabel: "Discharge Date",
  doctorPlaceholder: "Select Discharging Clinician",
  detailsTitle: "Discharge Summary Clinical Details",
  viewEmptyValueText: "Not documented in discharge summary",
  includeDoctor: true,
  requireDoctor: true,
  doctorLabel: "Discharge Author",
  requireDate: true,
  fileLabel: "Discharge Summary Attachment (Optional PDF/Image)",
  includeMedicines: true,
  medicinesLabel: "Discharge Medication Plan",
  emptyMedicinesText: "No discharge medications added yet.",
  emptyMedicinesViewText: "No discharge medications documented.",
  medicineNamePlaceholder: "Search or enter discharge medicine",
  medicineInstructionPlaceholder:
    "Dose, route, frequency, duration, and caution advice",
  tableColumns: [
    {
      key: "consultationDate",
      label: "Discharge Date",
      render: (item, helpers) => helpers.formatDate(item?.consultationDate),
    },
    {
      key: "doctor",
      label: "Discharge Author",
      render: (item, helpers) => helpers.getDoctorNameById(item?.doctor),
    },
    {
      key: "chiefComplaint",
      label: "Primary Diagnosis",
      render: (item) => item?.chiefComplaint || "-",
    },
    {
      key: "followUp",
      label: "Follow-up Plan",
      render: (item) => item?.followUp || "-",
    },
  ],
  formSections: [
    {
      title: "Reason for Admission and Diagnosis",
      description: "Primary diagnosis and summary.",
      fieldKeys: ["chiefComplaint", "remark"],
    },
    {
      title: "Clinical History and Condition",
      description: "History, allergies, and discharge condition.",
      fieldKeys: [
        "historyPresentIllness",
        "pastHistory",
        "familyHistory",
        "physicalExam",
        "allergy",
      ],
    },
    {
      title: "Inpatient Course",
      description: "Investigations and procedures during stay.",
      fieldKeys: ["investigationAdvice", "procedure"],
    },
    {
      title: "Discharge Instructions",
      description: "Follow-up and home care advice.",
      fieldKeys: ["followUp"],
    },
  ],
  viewSections: [
    {
      title: "Reason for Admission and Diagnosis",
      fieldKeys: ["chiefComplaint", "remark"],
    },
    {
      title: "Clinical History and Condition",
      fieldKeys: [
        "historyPresentIllness",
        "pastHistory",
        "familyHistory",
        "physicalExam",
        "allergy",
      ],
    },
    {
      title: "Inpatient Course",
      fieldKeys: ["investigationAdvice", "procedure"],
    },
    {
      title: "Discharge Instructions",
      fieldKeys: ["followUp"],
    },
  ],
  fields: [
    {
      key: "chiefComplaint",
      label: "Primary Diagnosis / Reason for Admission",
      type: "textarea",
      rows: 3,
      required: true,
      placeholder: "Enter primary diagnosis and admission reason.",
    },
    {
      key: "historyPresentIllness",
      label: "Brief Clinical History of Present Illness",
      type: "textarea",
      rows: 3,
      placeholder: "Brief timeline of current illness.",
    },
    {
      key: "pastHistory",
      label: "Relevant Past Medical and Surgical History",
      type: "textarea",
      rows: 3,
      placeholder: "Key comorbidities, prior admissions, surgeries.",
    },
    {
      key: "familyHistory",
      label: "Relevant Family History",
      type: "textarea",
      rows: 2,
      placeholder: "Relevant hereditary/family risk factors.",
    },
    {
      key: "physicalExam",
      label: "Condition at Discharge (Examination Findings)",
      type: "textarea",
      rows: 3,
      placeholder: "Key exam findings and condition at discharge.",
    },
    {
      key: "investigationAdvice",
      label: "Key Investigations and Significant Results",
      type: "textarea",
      rows: 3,
      placeholder: "Important investigations and results.",
    },
    {
      key: "allergy",
      label: "Allergy and Adverse Drug Reaction Status",
      type: "textarea",
      rows: 2,
      placeholder: "Known allergies/ADRs or no known allergies.",
    },
    {
      key: "procedure",
      label: "Procedures / Interventions During Stay",
      type: "textarea",
      rows: 3,
      placeholder: "Procedures or interventions completed.",
    },
    {
      key: "followUp",
      label: "Discharge Instructions and Follow-up Plan",
      type: "textarea",
      rows: 3,
      placeholder: "Follow-up schedule and home care advice.",
    },
    {
      key: "remark",
      label: "Hospital Course and Discharge Clinical Summary",
      type: "textarea",
      rows: 4,
      required: true,
      placeholder: "Concise summary of hospital course and outcome.",
    },
  ],
  summaryColumn: {
    label: "Primary Diagnosis",
    render: (item) => item?.chiefComplaint || "-",
  },
  buildDisplay: (report) =>
    `Discharge Summary - ${
      report?.consultationDate
        ? new Date(report.consultationDate).toLocaleDateString("en-GB")
        : "-"
    }`,
};

export const healthDocumentConfig = {
  title: "Health Document",
  reportType: "Health Document",
  hiType: "HealthDocumentRecord",
  prefix: "HD",
  dateLabel: "Document Date",
  includeDoctor: true,
  requireDoctor: false,
  doctorLabel: "Documenting Clinician (Optional)",
  doctorPlaceholder: "Select clinician (optional)",
  authorSectionTitle: "Authoring Context",
  authorSectionDescription:
    "Select how the document author should appear in the ABHA health record.",
  authorModeLabel: "Author Source",
  authorModeDoctorValue: "clinician",
  authorModeDefault: "organization",
  authorModeFallback: "organization",
  authorFallbackInfo:
    "This record will be authored as facility (Organization) unless a clinician is selected.",
  authorModeOptions: [
    {
      value: "clinician",
      label: "Clinician (Practitioner)",
    },
    {
      value: "organization",
      label: "Facility (Organization)",
    },
  ],
  tableDoctorLabel: "Author",
  doctorFallbackLabel: "Facility",
  requireDate: true,
  fileRequired: true,
  fileLabel: "Health Documents (PDF/Image)",
  tableColumns: [
    {
      key: "consultationDate",
      label: "Document Date",
      render: (item, helpers) => helpers.formatDate(item?.consultationDate),
    },
    {
      key: "doctor",
      label: "Author",
      render: (item, helpers) => {
        const doctorName = helpers.getDoctorNameById(item?.doctor);
        return doctorName === "-" ? "Facility" : doctorName;
      },
    },
    {
      key: "reportTitle",
      label: "Document Title",
      render: (item) => item?.reportTitle || "-",
    },
  ],
  fields: [
    {
      key: "reportTitle",
      label: "Document Title",
      required: true,
      placeholder: "e.g. Previous Discharge Record / External Lab Report",
    },
  ],
  summaryColumn: {
    label: "Document Title",
    render: (item) => item?.reportTitle || "-",
  },
  buildDisplay: (report) =>
    `Health Document - ${report?.reportTitle || "Uploaded document"} - ${
      report?.consultationDate
        ? new Date(report.consultationDate).toLocaleDateString("en-GB")
        : "-"
    }`,
};

export const immunizationRecordConfig = {
  title: "Immunization Record",
  reportType: "Immunization Record",
  hiType: "ImmunizationRecord",
  prefix: "IM",
  dateLabel: "Immunization Date",
  detailsTitle: "Immunization Clinical Details",
  viewEmptyValueText: "Not documented in immunization record",
  includeDoctor: true,
  requireDoctor: true,
  doctorLabel: "Authorizing Clinician",
  doctorPlaceholder: "Select authorizing clinician",
  requireDate: true,
  fileLabel: "Immunization Attachment (Optional PDF/Image)",
  tableColumns: [
    {
      key: "consultationDate",
      label: "Date",
      render: (item, helpers) => helpers.formatDate(item?.consultationDate),
    },
    {
      key: "serviceName",
      label: "Vaccine",
      render: (item) => item?.serviceName || "-",
    },
    {
      key: "dose",
      label: "Dose",
      render: (item) => {
        const value = String(item?.immunizationDoseValue || "").trim();
        const unit = String(item?.immunizationDoseUnit || "").trim();
        return value ? `${value}${unit ? ` ${unit}` : ""}` : "-";
      },
    },
    {
      key: "lot",
      label: "Lot No.",
      render: (item) => item?.immunizationLotNumber || "-",
    },
    {
      key: "nextDue",
      label: "Next Due",
      render: (item, helpers) => helpers.formatDate(item?.immunizationNextDueDate),
    },
    {
      key: "doctor",
      label: "Authorizing Clinician",
      render: (item, helpers) => {
        const doctorName = helpers.getDoctorNameById(item?.doctor);
        return doctorName === "-" ? "-" : doctorName;
      },
    },
    {
      key: "immunizationAppliedBy",
      label: "Applied By",
      render: (item) => item?.immunizationAppliedBy || "-",
    },
  ],
  formSections: [
    {
      title: "Administration Team",
      description: "Capture who authorized and who physically administered this dose.",
      fieldKeys: ["immunizationAppliedBy"],
    },
    {
      title: "Vaccine Details",
      description: "Capture product-level details for this administered dose.",
      fieldKeys: [
        "serviceName",
        "immunizationBrandName",
        "immunizationManufacturer",
        "immunizationLotNumber",
        "immunizationExpiryDate",
      ],
    },
    {
      title: "Administration Details",
      description: "Document how and where the dose was administered.",
      fieldKeys: [
        "immunizationDoseValue",
        "immunizationDoseUnit",
        "immunizationRoute",
        "immunizationSite",
      ],
    },
    {
      title: "Clinical Context",
      description: "Record reason, observation, and follow-up recommendation.",
      fieldKeys: [
        "immunizationReason",
        "immunizationObservation",
        "immunizationNextDueDate",
        "remark",
      ],
    },
  ],
  viewSections: [
    {
      title: "Administration Team",
      fieldKeys: ["immunizationAppliedBy"],
    },
    {
      title: "Vaccine Details",
      fieldKeys: [
        "serviceName",
        "immunizationBrandName",
        "immunizationManufacturer",
        "immunizationLotNumber",
        "immunizationExpiryDate",
      ],
    },
    {
      title: "Administration Details",
      fieldKeys: [
        "immunizationDoseValue",
        "immunizationDoseUnit",
        "immunizationRoute",
        "immunizationSite",
      ],
    },
    {
      title: "Clinical Context",
      fieldKeys: [
        "immunizationReason",
        "immunizationObservation",
        "immunizationNextDueDate",
        "remark",
      ],
    },
  ],
  fields: [
    {
      key: "serviceName",
      label: "Vaccine / Immunization Name",
      required: true,
      placeholder: "e.g. Hepatitis B, COVID-19 booster",
    },
    {
      key: "immunizationAppliedBy",
      label: "Applied By (Nurse/Clinician Name)",
      required: true,
      placeholder: "Enter person administering the vaccine",
    },
    {
      key: "immunizationBrandName",
      label: "Brand Name",
      placeholder: "e.g. Engerix-B",
    },
    {
      key: "immunizationManufacturer",
      label: "Manufacturer",
      placeholder: "e.g. Bharat Biotech / Serum Institute",
    },
    {
      key: "immunizationLotNumber",
      label: "Batch / Lot Number",
      placeholder: "Enter vaccine lot number",
    },
    {
      key: "immunizationExpiryDate",
      label: "Vaccine Expiry Date",
      type: "date",
      disableMax: true,
    },
    {
      key: "immunizationDoseValue",
      label: "Dose Quantity",
      type: "number",
      min: 0,
      step: "0.01",
      placeholder: "e.g. 0.5",
    },
    {
      key: "immunizationDoseUnit",
      label: "Dose Unit",
      type: "select",
      placeholder: "Select dose unit",
      options: [
        { label: "mL", value: "mL" },
        { label: "Dose", value: "dose" },
      ],
    },
    {
      key: "immunizationRoute",
      label: "Route of Administration",
      type: "select",
      placeholder: "Select route",
      options: [
        { label: "Intramuscular", value: "Intramuscular" },
        { label: "Subcutaneous", value: "Subcutaneous" },
        { label: "Intradermal", value: "Intradermal" },
        { label: "Oral", value: "Oral" },
        { label: "Intranasal", value: "Intranasal" },
      ],
    },
    {
      key: "immunizationSite",
      label: "Administration Site",
      type: "select",
      placeholder: "Select site",
      options: [
        { label: "Left deltoid", value: "Left deltoid" },
        { label: "Right deltoid", value: "Right deltoid" },
        { label: "Left thigh", value: "Left thigh" },
        { label: "Right thigh", value: "Right thigh" },
        { label: "Oral", value: "Oral" },
      ],
    },
    {
      key: "immunizationReason",
      label: "Reason for Immunization",
      type: "textarea",
      rows: 2,
      placeholder: "e.g. Scheduled booster / catch-up immunization",
    },
    {
      key: "immunizationObservation",
      label: "Immediate Observation",
      type: "textarea",
      rows: 2,
      placeholder: "Post-vaccination observation or adverse event notes",
    },
    {
      key: "immunizationNextDueDate",
      label: "Next Dose Due Date (Optional)",
      type: "date",
      disableMax: true,
    },
    {
      key: "remark",
      label: "Immunization Notes",
      type: "textarea",
      rows: 3,
      placeholder: "Any additional clinical note for this immunization event",
    },
  ],
  summaryColumn: {
    label: "Vaccine",
    render: (item) => item?.serviceName || "-",
  },
  buildDisplay: (report) =>
    `Immunization Record - ${report?.serviceName || "Immunization"} - ${
      report?.consultationDate
        ? new Date(report.consultationDate).toLocaleDateString("en-GB")
        : "-"
    }`,
};

export const opConsultConfig = {
  title: "OP Consult",
  reportType: "OP Consult",
  hiType: "OPConsultation",
  prefix: "OP",
  compactTable: true,
  dateLabel: "Consultation Date",
  doctorPlaceholder: "Select Consulting Clinician",
  detailsTitle: "OP Consultation Clinical Details",
  viewEmptyValueText: "Not documented in consultation note",
  includeDoctor: true,
  requireDoctor: true,
  doctorLabel: "Consulting Doctor",
  requireDate: true,
  fileLabel: "OP Consultation Attachment (Optional PDF/Image)",
  includeMedicines: true,
  medicinesOptional: true,
  medicinesLabel: "Medication Plan (Optional)",
  emptyMedicinesText: "No consultation medications added yet.",
  emptyMedicinesViewText: "No consultation medications documented.",
  medicineNamePlaceholder: "Search or enter prescribed medicine",
  medicineInstructionPlaceholder:
    "Dose, route, frequency, duration, and instruction",
  tableColumns: [
    {
      key: "consultationDate",
      label: "Consult Date",
      render: (item, helpers) => helpers.formatDate(item?.consultationDate),
    },
    {
      key: "chiefComplaint",
      label: "Chief Complaint",
      maxChars: 30,
      render: (item) => item?.chiefComplaint || "-",
    },
    {
      key: "carePlanSummary",
      label: "Care Plan",
      maxChars: 34,
      render: (item) => {
        const medicineCount = Array.isArray(item?.medicines)
          ? item.medicines.filter(
              (medicine) =>
                (typeof medicine?.name === "string" && medicine.name.trim()) ||
                (typeof medicine?.medicineName === "string" &&
                  medicine.medicineName.trim())
            ).length
          : 0;
        const careText =
          item?.referral ||
          item?.investigationAdvice ||
          item?.diagnosis ||
          item?.remark;

        if (medicineCount > 0 && careText) {
          return `${medicineCount} meds; ${careText}`;
        }
        if (medicineCount > 0) {
          return `${medicineCount} meds`;
        }
        return careText || "-";
      },
    },
  ],
  formSections: [
    {
      title: "Patient Presentation",
      description: "Presenting symptoms and current history.",
      fieldKeys: [
        "chiefComplaint",
        "historyPresentIllness",
      ],
    },
    {
      title: "History and Examination",
      description: "Past/family history, allergy, and exam findings.",
      fieldKeys: ["pastHistory", "familyHistory", "physicalExam", "allergy"],
    },
    {
      title: "Assessment and Orders",
      description: "Diagnosis, investigations, procedures, observations.",
      fieldKeys: ["diagnosis", "investigationAdvice", "procedure", "otherObservation"],
    },
    {
      title: "Plan and Continuity",
      description: "Follow-up, referral, and final note.",
      fieldKeys: ["followUp", "referral", "remark"],
    },
  ],
  viewSections: [
    {
      title: "Patient Presentation",
      fieldKeys: [
        "chiefComplaint",
        "historyPresentIllness",
      ],
    },
    {
      title: "History and Examination",
      fieldKeys: ["pastHistory", "familyHistory", "physicalExam", "allergy"],
    },
    {
      title: "Assessment and Orders",
      fieldKeys: ["diagnosis", "investigationAdvice", "procedure", "otherObservation"],
    },
    {
      title: "Plan and Continuity",
      fieldKeys: ["followUp", "referral", "remark"],
    },
  ],
  fields: [
    {
      key: "chiefComplaint",
      label: "Presenting Complaints",
      type: "textarea",
      rows: 3,
      required: true,
      placeholder: "Primary presenting complaints.",
    },
    {
      key: "historyPresentIllness",
      label: "History of Present Illness (HPI)",
      type: "textarea",
      rows: 3,
      placeholder: "Onset, duration, progression, associated symptoms.",
    },
    {
      key: "pastHistory",
      label: "Past Medical and Surgical History",
      type: "textarea",
      rows: 3,
      placeholder: "Key comorbidities, prior care, surgeries.",
    },
    {
      key: "familyHistory",
      label: "Relevant Family History",
      type: "textarea",
      rows: 2,
      placeholder: "Relevant hereditary/family conditions.",
    },
    {
      key: "physicalExam",
      label: "Physical Examination Findings",
      type: "textarea",
      rows: 3,
      placeholder: "Focused examination findings.",
    },
    {
      key: "investigationAdvice",
      label: "Investigation Orders / Advice",
      type: "textarea",
      rows: 3,
      placeholder: "Investigations advised.",
    },
    {
      key: "allergy",
      label: "Allergy and ADR History",
      type: "textarea",
      rows: 2,
      placeholder: "Known allergies/ADRs or no known allergies.",
    },
    {
      key: "followUp",
      label: "Follow-up and Review Plan",
      type: "textarea",
      rows: 3,
      placeholder: "Follow-up timeline and review advice.",
    },
    {
      key: "procedure",
      label: "Procedures / Interventions Performed",
      type: "textarea",
      rows: 3,
      placeholder: "Procedures/interventions done.",
    },
    {
      key: "referral",
      label: "Referral Advice",
      type: "textarea",
      rows: 3,
      placeholder: "Referral destination and reason.",
    },
    {
      key: "otherObservation",
      label: "Additional Clinical Observations",
      type: "textarea",
      rows: 3,
      placeholder: "Additional observations.",
    },
    {
      key: "diagnosis",
      label: "Clinical Diagnosis / Impression",
      type: "textarea",
      rows: 3,
      placeholder: "Provisional/final diagnosis.",
    },
    {
      key: "remark",
      label: "Consultation Summary Note",
      type: "textarea",
      rows: 3,
      placeholder: "Brief consult summary and plan.",
    },
  ],
  summaryColumn: {
    label: "Presenting Complaint",
    render: (item) => item?.chiefComplaint || "-",
  },
  buildDisplay: (report) =>
    `OP Consult - ${report?.chiefComplaint || "Consultation"} - ${
      report?.consultationDate
        ? new Date(report.consultationDate).toLocaleDateString("en-GB")
        : "-"
    }`,
};

export const invoiceRecordConfig = {
  title: "Invoice Record",
  reportType: "Invoice",
  hiType: "Invoice",
  prefix: "IN",
  includeDoctor: true,
  requireDoctor: true,
  doctorLabel: "Billing Author",
  requireDate: true,
  enableFileUpload: false,
  tableColumns: [
    {
      key: "invoiceId",
      label: "INVID",
      render: (item) => item?.invoiceId || "-",
    },
    {
      key: "consultationDate",
      label: "APPTDATE",
      render: (item, helpers) => helpers.formatDate(item?.consultationDate),
    },
    {
      key: "appointmentId",
      label: "APPTID",
      render: (item) => item?.appointmentId || "-",
    },
    {
      key: "invoiceAmount",
      label: "AMOUNT (₹)",
      render: (item) => {
        const amount = Number(item?.invoiceAmount);
        return Number.isFinite(amount) && amount >= 0 ? amount.toFixed(2) : "-";
      },
    },
  ],
  fields: [
    {
      key: "invoiceId",
      label: "Invoice ID (Optional)",
      placeholder: "Use DB invoice id or invoice number",
    },
    {
      key: "appointmentId",
      label: "Appointment ID (Optional)",
      placeholder: "Appointment identifier",
    },
    {
      key: "invoiceAmount",
      label: "Invoice Amount (Optional)",
      type: "number",
      min: 0,
      step: "0.01",
      placeholder: "0.00",
    },
  ],
  summaryColumn: {
    label: "Invoice Ref",
    render: (item) => item?.invoiceId || "Auto (Latest)",
  },
  buildDisplay: (report) =>
    `Invoice Record - ${report?.invoiceId || "Latest invoice"} - ${
      report?.consultationDate
        ? new Date(report.consultationDate).toLocaleDateString("en-GB")
        : "-"
    }`,
};
